import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from 'react'

/**
 * Reports whether the referenced element is currently clipping its text
 * horizontally — i.e. it carries `truncate` (or any `text-ellipsis`/
 * `overflow-hidden` combination) and the content does not fit.
 *
 * Why this exists: Epic can launch the SPA in a *sidebar* (a ~350px column)
 * rather than the full workspace, so sticky-header values like the patient name
 * are routinely ellipsised. We only want to attach a "reveal the full value"
 * tooltip when the value is *actually* clipped — an always-on tooltip over a
 * fully visible name is noise, and a `title`-style tooltip that repeats text the
 * user can already read trains people to ignore tooltips.
 *
 * Measurement runs in a layout effect (pre-paint, so the very first painted
 * frame already has the correct answer) and re-runs whenever the element or its
 * parent changes size. `ResizeObserver` is feature-detected to match
 * `ClinicalSummaryCard` / `orderSideNavModel`: jsdom (unit tests) and older
 * browsers fall back to the window `resize` event alone.
 */
export function useIsTruncated(ref: RefObject<HTMLElement | null>): boolean {
  const [isTruncated, setIsTruncated] = useState(false)

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    // Sub-pixel text layout can leave `scrollWidth` a hair above `clientWidth`
    // on content that visually fits; the 1px slack keeps us from showing a
    // tooltip that adds nothing. Matches the `+ 1` slack in ClinicalSummaryCard.
    const clipped = el.scrollWidth - el.clientWidth > 1
    // Only commit on a real flip so the unconditional layout effect below
    // converges instead of re-rendering forever.
    setIsTruncated((prev) => (prev === clipped ? prev : clipped))
  }, [ref])

  // Deliberately dependency-free: the *text* can change (patient name resolves,
  // demographics are edited) without the box ever resizing, and no observer
  // fires for a text-only change. Re-measuring every commit is a couple of
  // layout reads on a handful of header nodes.
  useLayoutEffect(measure)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }

    const ro = new ResizeObserver(measure)
    ro.observe(el)
    // The element is the truncating box, but its width is dictated by the flex
    // parent. Observing the parent as well catches the Epic sidebar being
    // dragged narrower in cases where the child's own box report is coalesced.
    if (el.parentElement) ro.observe(el.parentElement)
    return () => ro.disconnect()
  }, [ref, measure])

  return isTruncated
}
